/* ═══════════════════════════════════════════════════════════════
   TeamService — Team Member CRUD, Named Teams, Project Assignments
   via EC2 API (cloud, in-memory cache)
   ═══════════════════════════════════════════════════════════════ */

import type { TeamMember, ProjectAssignment, Team, TeamAssignment } from '../../shared/types';
import * as cache from './cloudCache';

export const TeamService = {
  // ─── Members ───

  list: async (): Promise<TeamMember[]> => cache.list<TeamMember>('team_members'),

  // firstName/middleName/lastName/secondaryEmail (People Rebuild, migration
  // 038) need no special handling here — cache.save's generic camelCase ->
  // snake_case conversion already ships them as first_name/middle_name/
  // last_name/secondary_email; the server (ec2-api/server.js
  // dbSaveTeamMember) splits them into member_profiles and rebuilds
  // team_members.name in the same transaction.
  save: async (member: TeamMember): Promise<TeamMember> => cache.save('team_members', member),

  /** Soft-deactivate — sets active=false, keeps the record/login row and
   *  all its history intact. This is the DEFAULT removal action for
   *  everyone who can remove a member at all, admins included (Team
   *  Compliance Sprint, Batch E — soft vs. hard used to be INFERRED from
   *  the caller's permission tier, so an admin asking to "deactivate"
   *  silently got a hard delete of a login row instead; now the caller
   *  states intent explicitly by calling this vs. `hardDelete` below).
   *  Returns `{ deactivated: true, member }`. */
  deactivate: async (
    id: string
  ): Promise<{ success?: boolean; deactivated?: boolean; member?: TeamMember } | void> =>
    cache.deactivate('team_members', id),

  /** Explicit HARD delete — permanently removes the person's record AND
   *  login (team_members is the login table: password_hash/auth_method
   *  live here). Only ever authorized for admin_center.manage_roles
   *  holders, and only when the caller states this exact intent (the
   *  server requires confirmHardDelete:true — see ec2-api/server.js
   *  checkTableAction). Never call this for "deactivate"; use
   *  TeamService.deactivate instead — it remains available to admins too. */
  hardDelete: async (id: string): Promise<{ success?: boolean } | void> =>
    cache.remove('team_members', id, { confirmHardDelete: true }),

  // ─── Project Assignments ───

  listAssignments: async (
    teamMemberId?: string,
    projectId?: string
  ): Promise<ProjectAssignment[]> => {
    const filters: Record<string, any> = {};
    if (teamMemberId) filters.teamMemberId = teamMemberId;
    if (projectId) filters.projectId = projectId;
    if (Object.keys(filters).length > 0) {
      return cache.query<ProjectAssignment>('project_assignments', filters);
    }
    return cache.list<ProjectAssignment>('project_assignments');
  },

  assign: async (
    projectId: string,
    teamMemberId: string,
    role?: string
  ): Promise<ProjectAssignment> => {
    const assignment: ProjectAssignment = {
      id: '',
      projectId,
      teamMemberId,
      role: role || undefined,
      createdAt: new Date().toISOString(),
    };
    return cache.save('project_assignments', assignment);
  },

  unassign: async (id: string): Promise<void> => cache.remove('project_assignments', id),

  // ─── Named Teams ───

  listTeams: async (): Promise<Team[]> => cache.list<Team>('teams'),

  saveTeam: async (team: Team): Promise<Team> => {
    // Strip any underscore-prefixed synthetic fields (e.g. row-aggregate
    // helpers like `_memberCount`, `_leaderIds`) before persisting. The
    // server's dbSave blindly inserts every key as a column, so a
    // synthetic field would throw `column "_member_count" of relation
    // "teams" does not exist` and the page crashes after save.
    const clean: Record<string, any> = {};
    for (const [k, v] of Object.entries(team)) {
      if (!k.startsWith('_')) clean[k] = v;
    }
    return cache.save('teams', clean as Team);
  },

  deleteTeam: async (id: string): Promise<void> => cache.remove('teams', id),

  // ─── Team Assignments (member <-> team) ───

  listTeamAssignments: async (teamId?: string, memberId?: string): Promise<TeamAssignment[]> => {
    const filters: Record<string, any> = {};
    if (teamId) filters.teamId = teamId;
    if (memberId) filters.teamMemberId = memberId;
    if (Object.keys(filters).length > 0) {
      return cache.query<TeamAssignment>('team_assignments', filters);
    }
    return cache.list<TeamAssignment>('team_assignments');
  },

  assignToTeam: async (
    teamId: string,
    memberId: string,
    role: 'leader' | 'member' = 'member'
  ): Promise<TeamAssignment> => {
    // Check for existing assignment to prevent duplicates
    const existing = (await cache.list<TeamAssignment>('team_assignments')).find(
      (a) => a.teamId === teamId && a.teamMemberId === memberId
    );
    if (existing) {
      // Already assigned — update role if different
      if (existing.teamRole !== role) {
        return cache.save('team_assignments', { ...existing, teamRole: role });
      }
      return existing;
    }
    const assignment: TeamAssignment = {
      id: '',
      teamId,
      teamMemberId: memberId,
      teamRole: role,
      createdAt: new Date().toISOString(),
    };
    return cache.save('team_assignments', assignment);
  },

  updateTeamAssignment: async (assignment: TeamAssignment): Promise<TeamAssignment> =>
    cache.save('team_assignments', assignment),

  removeFromTeam: async (id: string): Promise<void> => cache.remove('team_assignments', id),
};
